import React, { useEffect, useState, useRef } from 'react';
import axios from '../axiosInstance';
import { motion } from 'framer-motion';
import { FiX } from 'react-icons/fi';

export default function WarehouseInventory() {
  const [inventory, setInventory] = useState([]);

  // Warehouse filter states
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState('');
  const [warehouseSearch, setWarehouseSearch] = useState('');
  const [warehouseDropdownOpen, setWarehouseDropdownOpen] = useState(false);

  // Commodity filter states
  const [commodities, setCommodities] = useState([]);
  const [selectedCommodity, setSelectedCommodity] = useState('');
  const [commoditySearch, setCommoditySearch] = useState('');
  const [commodityDropdownOpen, setCommodityDropdownOpen] = useState(false);

  // Scheme filter states
  const [schemes, setSchemes] = useState([]);
  const [selectedScheme, setSelectedScheme] = useState('');
  const [schemeSearch, setSchemeSearch] = useState('');
  const [schemeDropdownOpen, setSchemeDropdownOpen] = useState(false);

  // State filter states
  const [states, setStates] = useState([]);
  const [selectedState, setSelectedState] = useState('');
  const [stateSearch, setStateSearch] = useState('');
  const [stateDropdownOpen, setStateDropdownOpen] = useState(false);

  // Season filter states
  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState('');
  const [seasonSearch, setSeasonSearch] = useState('');
  const [seasonDropdownOpen, setSeasonDropdownOpen] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const [selectedRows, setSelectedRows] = useState([]);

  const token = localStorage.getItem('token');

  // User role and state for branch users
  const [role, setRole] = useState('');
  const [userStateId, setUserStateId] = useState('');

  // Fetch inventory
  const fetchInventory = async (
    pageNum = 1,
    limitNum = 10,
    warehouseId = '',
    commodityId = '',
    schemeId = '',
    stateId = '',
    seasonId = ''
  ) => {
    setLoading(true);
    try {
      let url = `inventory/warehouse-inventory?page=${pageNum}&limit=${limitNum}`;
      if (warehouseId) url += `&warehouse_id=${warehouseId}`;
      if (commodityId) url += `&commodity_id=${commodityId}`;
      if (schemeId) url += `&scheme_id=${schemeId}`;
      if (stateId) url += `&state_id=${stateId}`;
      if (seasonId) url += `&season_id=${seasonId}`;

      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setInventory(res.data.data || []);
      setTotal(res.data.total || res.data.data?.length || 0);
      setError('');
    } catch (err) {
      console.error(
        'Error fetching inventory:',
        err.response?.data || err.message
      );
      setError('⚠️ Failed to fetch warehouse inventory.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch warehouses list for dropdown
  const fetchWarehouses = async () => {
    try {
      const res = await axios.get('warehouse?paginate=false', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setWarehouses(res.data || []);
    } catch (err) {
      console.error('Error fetching warehouses:', err.message);
    }
  };

  // Fetch commodities
  const fetchCommodities = async () => {
    try {
      const res = await axios.get('commodity', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCommodities(res.data || []);
    } catch (err) {
      console.error('Error fetching commodities:', err.message);
    }
  };

  // Fetch schemes
  const fetchSchemes = async () => {
    try {
      const res = await axios.get('schemes', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSchemes(res.data || []);
    } catch (err) {
      console.error('Error fetching schemes:', err.message);
    }
  };

  // Fetch states
  const fetchStates = async () => {
    try {
      const res = await axios.get('states', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStates(res.data || []);
    } catch (err) {
      console.error('Error fetching states:', err.message);
    }
  };

  // Fetch seasons
  const fetchSeasons = async () => {
    try {
      const res = await axios.get('seasons?paginate=false', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSeasons(res.data || []);
    } catch (err) {
      console.error('Error fetching seasons:', err.message);
    }
  };

  // Filter warehouses with search text and selected state
  const filteredWarehouses = warehouses.filter((wh) => {
    const matchesSearch = (wh?.warehouse_name || '')
      .toLowerCase()
      .includes(warehouseSearch.toLowerCase());
    const matchesState = selectedState ? wh?.state?.id === selectedState : true;
    return matchesSearch && matchesState;
  });

  // Filter commodities with search text
  const filteredCommodities = commodities.filter((c) =>
    (c?.commodityname || '')
      .toLowerCase()
      .includes(commoditySearch.toLowerCase())
  );

  // Filter schemes with search text
  const filteredSchemes = schemes.filter((s) =>
    (s?.name || '').toLowerCase().includes(schemeSearch.toLowerCase())
  );

  // Filter states with search text
  const filteredStates = states.filter((s) =>
    (s?.name || '').toLowerCase().includes(stateSearch.toLowerCase())
  );

  // Filter seasons with search text
  const filteredSeasons = seasons.filter((s) =>
    (s?.name || '').toLowerCase().includes(seasonSearch.toLowerCase())
  );

  // Clear all filters
  const clearFilters = () => {
    setSelectedWarehouse('');
    setWarehouseSearch('');
    setSelectedCommodity('');
    setCommoditySearch('');
    setSelectedScheme('');
    setSchemeSearch('');
    setSelectedState('');
    setStateSearch('');
    setSelectedSeason('');
    setSeasonSearch('');
    setPage(1);
  };

  // Ref to track if initial data has been fetched
  const initialDataFetchedRef = useRef(false);

  useEffect(() => {
    if (!initialDataFetchedRef.current) {
      initialDataFetchedRef.current = true;
      // Don't call fetchInventory here as it will be called by the dependency-based useEffect
      fetchWarehouses();
      fetchCommodities();
      fetchSchemes();
      fetchStates();
      fetchSeasons();

      // Get user role and state from localStorage
      const user = JSON.parse(localStorage.getItem('user'));
      const userRole = user?.roles?.[0]?.name || '';
      const stateId = user?.state?.id || '';
      const stateName = user?.state?.name || '';
      setRole(userRole);
      setUserStateId(stateId);

      // Auto-set state filter for Branch users
      if (userRole === 'Branch' && stateId) {
        setSelectedState(stateId);
        setStateSearch(stateName);
      }
    }
  }, []);

  // Ref to prevent duplicate API calls when filters change
  const fetchDataRef = useRef(null);

  useEffect(() => {
    // Clear any pending timeouts
    if (fetchDataRef.current) {
      clearTimeout(fetchDataRef.current);
    }

    // Set a small timeout to debounce the API call
    fetchDataRef.current = setTimeout(() => {
      fetchInventory(
        page,
        limit,
        selectedWarehouse,
        selectedCommodity,
        selectedScheme,
        selectedState,
        selectedSeason
      );
    }, 100);

    // Cleanup function to clear timeout
    return () => {
      if (fetchDataRef.current) {
        clearTimeout(fetchDataRef.current);
      }
    };
  }, [
    page,
    selectedWarehouse,
    selectedCommodity,
    selectedScheme,
    selectedState,
    selectedSeason,
  ]);

  const totalPages = Math.ceil(total / limit);

  // Export all warehouse inventory (Uses GET with filters)
  const handleExportAll = async () => {
    try {
      setLoading(true);
      let queryParams = new URLSearchParams();
      if (selectedWarehouse) queryParams.append('warehouse_id', selectedWarehouse);
      if (selectedCommodity) queryParams.append('commodity_id', selectedCommodity);
      if (selectedScheme) queryParams.append('scheme_id', selectedScheme);
      if (selectedState) queryParams.append('state_id', selectedState);
      if (selectedSeason) queryParams.append('season_id', selectedSeason);

      const res = await axios.get(
        `inventory/export-warehousewise-excel?${queryParams.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        }
      );

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `warehouse-inventory-all-${new Date().toISOString().split('T')[0]}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      alert('✅ All inventory exported successfully');
    } catch (err) {
      console.error('Export all failed:', err);
      alert('⚠️ Export failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Export only selected rows (Uses POST with items in body)
  const handleExportSelected = async () => {
    if (selectedRows.length === 0) {
      alert('Please select at least one row to export.');
      return;
    }

    try {
      setLoading(true);
      const payload = {
        items: selectedRows.map((row) => ({
          warehouseCode: row.warehouse_code?.trim(),
          commodity: row.commodity_name?.trim(),
          scheme: row.scheme_code?.trim(),
          season: row.season_code?.trim(),
          state: row.state_name?.trim(),
        })),
      };

      const res = await axios.post('inventory/export-selected', payload, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `selected-warehouse-inventory-${new Date().toISOString().split('T')[0]}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      alert('✅ Selected records exported successfully');
    } catch (err) {
      console.error('Export selected failed:', err);
      alert('⚠️ Export selected failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const totalSelectedAmount = selectedRows.reduce(
    (sum, row) => sum + parseFloat(row.total_current_quantity || 0),
    0
  );

  const handleSelectRow = (inv) => {
    setSelectedRows((prev) => {
      const isSelected = prev.some((row) => row.warehouse_code === inv.warehouse_code && row.commodity_name === inv.commodity_name);
      if (isSelected) {
        return prev.filter((row) => !(row.warehouse_code === inv.warehouse_code && row.commodity_name === inv.commodity_name));
      } else {
        return [...prev, inv];
      }
    });
  };

  const handleSelectAll = () => {
    const allOnPageSelected = inventory.length > 0 && inventory.every((inv) =>
      selectedRows.some((row) => row.warehouse_code === inv.warehouse_code && row.commodity_name === inv.commodity_name)
    );

    if (allOnPageSelected) {
      setSelectedRows((prev) =>
        prev.filter(
          (row) =>
            !inventory.some(
              (inv) => inv.warehouse_code === row.warehouse_code && inv.commodity_name === row.commodity_name
            )
        )
      );
    } else {
      setSelectedRows((prev) => {
        const newSelections = inventory.filter(
          (inv) =>
            !prev.some(
              (row) => row.warehouse_code === inv.warehouse_code && row.commodity_name === inv.commodity_name
            )
        );
        return [...prev, ...newSelections];
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-blue-50 to-blue-100 p-10  text-gray-900">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-6 md:mb-10 gap-4">
        <h2 className="text-3xl md:text-4xl font-extrabold text-blue-800 tracking-tight">
          📦 Warehouse Wise Stock
        </h2>
      </div>

      {/* Filter and Actions */}
      <div className="flex flex-wrap gap-4 mb-6">
        {/* State Dropdown */}
        <div className="relative w-[250px] max-w-full">
          <div
            onClick={() => {
              // Close all other dropdowns first
              setCommodityDropdownOpen(false);
              setSchemeDropdownOpen(false);
              setSeasonDropdownOpen(false);
              setWarehouseDropdownOpen(false);
              setStateDropdownOpen((prev) => !prev);
            }}
            className="px-4 py-2 border rounded-xl bg-white shadow-sm cursor-pointer text-base flex justify-between items-center h-[50px]"
          >
            <span>
              {selectedState ? stateSearch || 'State Selected' : 'Select State'}
            </span>
            <div className="flex items-center gap-2">
              {selectedState && (
                <FiX
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedState('');
                    setStateSearch('');
                    setPage(1);
                  }}
                  className="text-gray-400 hover:text-red-500 text-lg cursor-pointer"
                  title="Clear"
                />
              )}
              <span className="text-gray-500">
                {stateDropdownOpen ? '▲' : '▼'}
              </span>
            </div>
          </div>

          {stateDropdownOpen && (
            <div className="absolute top-full left-0 right-0 bg-white border rounded-xl shadow-lg max-h-72 overflow-y-auto mt-1 z-10">
              <input
                type="text"
                placeholder="Search State..."
                value={stateSearch}
                onChange={(e) => setStateSearch(e.target.value)}
                className="w-full px-3 py-2 border-b text-sm focus:outline-none"
              />
              {filteredStates.length === 0 && (
                <p className="p-2 text-gray-500">No states found</p>
              )}
              {filteredStates.map((s) => (
                <div
                  key={s.id}
                  onClick={() => {
                    setSelectedState(s.id);
                    setStateSearch(s.name);
                    setStateDropdownOpen(false);
                    setPage(1);
                  }}
                  className={`p-2 cursor-pointer hover:bg-blue-100 ${selectedState === s.id ? 'bg-blue-200 font-semibold' : ''
                    }`}
                >
                  {s.name}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Commodity Dropdown */}
        <div className="relative w-[250px] max-w-full">
          <div
            onClick={() => {
              // Close all other dropdowns first
              setStateDropdownOpen(false);
              setSchemeDropdownOpen(false);
              setSeasonDropdownOpen(false);
              setWarehouseDropdownOpen(false);
              setCommodityDropdownOpen((prev) => !prev);
            }}
            className="px-4 py-2 border rounded-xl bg-white shadow-sm cursor-pointer text-base flex justify-between items-center h-[50px]"
          >
            <span>
              {selectedCommodity
                ? commoditySearch || 'Commodity Selected'
                : 'Select Commodity'}
            </span>
            <div className="flex items-center gap-2">
              {selectedCommodity && (
                <FiX
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedCommodity('');
                    setCommoditySearch('');
                    setPage(1);
                  }}
                  className="text-gray-400 hover:text-red-500 text-lg cursor-pointer"
                  title="Clear"
                />
              )}
              <span className="text-gray-500">
                {commodityDropdownOpen ? '▲' : '▼'}
              </span>
            </div>
          </div>

          {commodityDropdownOpen && (
            <div className="absolute top-full left-0 right-0 bg-white border rounded-xl shadow-lg max-h-72 overflow-y-auto mt-1 z-10">
              <input
                type="text"
                placeholder="Search Commodity..."
                value={commoditySearch}
                onChange={(e) => setCommoditySearch(e.target.value)}
                className="w-full px-3 py-2 border-b text-sm focus:outline-none"
              />
              {filteredCommodities.length === 0 && (
                <p className="p-2 text-gray-500">No commodities found</p>
              )}
              {filteredCommodities.map((c) => (
                <div
                  key={c.id}
                  onClick={() => {
                    setSelectedCommodity(c.id);
                    setCommoditySearch(c.commodityname);
                    setCommodityDropdownOpen(false);
                    setPage(1);
                  }}
                  className={`p-2 cursor-pointer hover:bg-blue-100 ${selectedCommodity === c.id
                    ? 'bg-blue-200 font-semibold'
                    : ''
                    }`}
                >
                  {c.commodityname}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Scheme Dropdown */}
        <div className="relative w-[250px] max-w-full">
          <div
            onClick={() => {
              // Close all other dropdowns first
              setStateDropdownOpen(false);
              setCommodityDropdownOpen(false);
              setSeasonDropdownOpen(false);
              setWarehouseDropdownOpen(false);
              setSchemeDropdownOpen((prev) => !prev);
            }}
            className="px-4 py-2 border rounded-xl bg-white shadow-sm cursor-pointer text-base flex justify-between items-center h-[50px]"
          >
            <span>
              {selectedScheme
                ? schemeSearch || 'Scheme Selected'
                : 'Select Scheme'}
            </span>
            <div className="flex items-center gap-2">
              {selectedScheme && (
                <FiX
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedScheme('');
                    setSchemeSearch('');
                    setPage(1);
                  }}
                  className="text-gray-400 hover:text-red-500 text-lg cursor-pointer"
                  title="Clear"
                />
              )}
              <span className="text-gray-500">
                {schemeDropdownOpen ? '▲' : '▼'}
              </span>
            </div>
          </div>

          {schemeDropdownOpen && (
            <div className="absolute top-full left-0 right-0 bg-white border rounded-xl shadow-lg max-h-72 overflow-y-auto mt-1 z-10">
              <input
                type="text"
                placeholder="Search Scheme..."
                value={schemeSearch}
                onChange={(e) => setSchemeSearch(e.target.value)}
                className="w-full px-3 py-2 border-b text-sm focus:outline-none"
              />
              {filteredSchemes.length === 0 && (
                <p className="p-2 text-gray-500">No schemes found</p>
              )}
              {filteredSchemes.map((s) => (
                <div
                  key={s.id}
                  onClick={() => {
                    setSelectedScheme(s.id);
                    setSchemeSearch(s.name);
                    setSchemeDropdownOpen(false);
                    setPage(1);
                  }}
                  className={`p-2 cursor-pointer hover:bg-blue-100 ${selectedScheme === s.id ? 'bg-blue-200 font-semibold' : ''
                    }`}
                >
                  {s.name}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Season Dropdown */}
        <div className="relative w-[250px] max-w-full">
          <div
            onClick={() => {
              // Close all other dropdowns first
              setStateDropdownOpen(false);
              setCommodityDropdownOpen(false);
              setSchemeDropdownOpen(false);
              setWarehouseDropdownOpen(false);
              setSeasonDropdownOpen((prev) => !prev);
            }}
            className="px-4 py-2 border rounded-xl bg-white shadow-sm cursor-pointer text-base flex justify-between items-center h-[50px]"
          >
            <span>
              {selectedSeason
                ? seasonSearch || 'Season Selected'
                : 'Select Season'}
            </span>
            <div className="flex items-center gap-2">
              {selectedSeason && (
                <FiX
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedSeason('');
                    setSeasonSearch('');
                    setPage(1);
                  }}
                  className="text-gray-400 hover:text-red-500 text-lg cursor-pointer"
                  title="Clear"
                />
              )}
              <span className="text-gray-500">
                {seasonDropdownOpen ? '▲' : '▼'}
              </span>
            </div>
          </div>

          {seasonDropdownOpen && (
            <div className="absolute top-full left-0 right-0 bg-white border rounded-xl shadow-lg max-h-72 overflow-y-auto mt-1 z-10">
              <input
                type="text"
                placeholder="Search Season..."
                value={seasonSearch}
                onChange={(e) => setSeasonSearch(e.target.value)}
                className="w-full px-3 py-2 border-b text-sm focus:outline-none"
              />
              {filteredSeasons.length === 0 && (
                <p className="p-2 text-gray-500">No seasons found</p>
              )}
              {filteredSeasons.map((s) => (
                <div
                  key={s.id}
                  onClick={() => {
                    setSelectedSeason(s.id);
                    setSeasonSearch(s.name);
                    setSeasonDropdownOpen(false);
                    setPage(1);
                  }}
                  className={`p-2 cursor-pointer hover:bg-blue-100 ${selectedSeason === s.id ? 'bg-blue-200 font-semibold' : ''
                    }`}
                >
                  {s.name}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Warehouse Dropdown with Search */}
        <div className="relative w-[250px] max-w-full">
          <div
            onClick={() => {
              // Close all other dropdowns first
              setStateDropdownOpen(false);
              setCommodityDropdownOpen(false);
              setSchemeDropdownOpen(false);
              setSeasonDropdownOpen(false);
              setWarehouseDropdownOpen((prev) => !prev);
            }}
            className="px-4 py-2 border rounded-xl bg-white shadow-sm cursor-pointer text-base flex justify-between items-center h-[50px]"
          >
            <span>
              {selectedWarehouse
                ? warehouseSearch || 'Warehouse Selected'
                : 'Select Warehouse'}
            </span>
            <div className="flex items-center gap-2">
              {selectedWarehouse && (
                <FiX
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedWarehouse('');
                    setWarehouseSearch('');
                    setPage(1);
                  }}
                  className="text-gray-400 hover:text-red-500 text-lg cursor-pointer"
                  title="Clear"
                />
              )}
              <span className="text-gray-500">
                {warehouseDropdownOpen ? '▲' : '▼'}
              </span>
            </div>
          </div>

          {warehouseDropdownOpen && (
            <div className="absolute top-full left-0 right-0 bg-white border rounded-xl shadow-lg max-h-72 overflow-y-auto mt-1 z-10">
              <input
                type="text"
                placeholder="Search Warehouse..."
                value={warehouseSearch}
                onChange={(e) => setWarehouseSearch(e.target.value)}
                className="w-full px-3 py-2 border-b text-sm focus:outline-none"
              />
              {filteredWarehouses.length === 0 && (
                <p className="p-2 text-gray-500">No warehouses found</p>
              )}
              {filteredWarehouses.map((wh) => (
                <div
                  key={wh.id}
                  onClick={() => {
                    setSelectedWarehouse(wh.id);
                    setWarehouseSearch(wh.warehouse_name);
                    setWarehouseDropdownOpen(false);
                    setPage(1);
                  }}
                  className={`p-2 cursor-pointer hover:bg-blue-100 ${selectedWarehouse === wh.id
                    ? 'bg-blue-200 font-semibold'
                    : ''
                    }`}
                >
                  {wh.warehouse_name}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex gap-3">
          <button
            onClick={clearFilters}
            className="bg-gray-500 text-white px-4 py-2 rounded-lg shadow-md hover:bg-gray-600 flex items-center gap-1"
          >
            🔄 Clear Filters
          </button>

          <button
            onClick={handleExportAll}
            disabled={loading}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg shadow-md hover:bg-purple-700 disabled:bg-purple-400 disabled:cursor-not-allowed flex items-center gap-1"
          >
            {loading ? 'Exporting...' : '📥 Export All'}
          </button>

          <button
            onClick={handleExportSelected}
            disabled={loading || selectedRows.length === 0}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg shadow-md hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed flex items-center gap-1"
          >
            {loading ? 'Exporting...' : `📤 Export Selected (${selectedRows.length})`}
          </button>
        </div>
      </div>

      {/* Selected Amount Card */}
      {selectedRows.length > 0 && (
        <div className="mb-6 bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-xl shadow-sm flex justify-between items-center">
          <div>
            <p className="text-sm text-blue-600 font-medium uppercase tracking-wider">Total Selected Amount</p>
            <p className="text-2xl font-bold text-blue-800">{totalSelectedAmount.toFixed(4)} MT</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-blue-600 font-medium uppercase tracking-wider">Selected Rows</p>
            <p className="text-2xl font-bold text-blue-800">{selectedRows.length}</p>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 text-lg p-4 rounded mb-6 shadow">
          {error}
        </div>
      )}

      {/* Table */}
      <motion.div className="overflow-hidden rounded-2xl shadow-xl border border-gray-100 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead className="bg-gray-100 border-b border-gray-200">
              <tr>
                {[
                  'Select',
                  'State',
                  'District',
                  'Commodity',
                  'Scheme',
                  'Season',
                  'Warehouse',
                  'Age of Stock (Days)',
                  'Qty(MT)',
                  'Total Bags',
                ].map((header) => (
                  <th
                    key={header}
                    className="py-4 px-6 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider"
                  >
                    {header === 'Select' ? (
                      <input
                        type="checkbox"
                        className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        onChange={handleSelectAll}
                        checked={
                          inventory.length > 0 &&
                          inventory.every((inv) =>
                            selectedRows.some((row) => row.warehouse_code === inv.warehouse_code && row.commodity_name === inv.commodity_name)
                          )
                        }
                      />
                    ) : (
                      header
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan="13" className="text-center py-10 text-blue-600 font-semibold">
                    ⏳ Loading inventory...
                  </td>
                </tr>
              ) : inventory.length > 0 ? (
                inventory.map((inv) => (
                  <motion.tr
                    key={`${inv.warehouse_code}-${inv.commodity_name}`}
                    className="hover:bg-gray-50 transition-colors duration-200"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                  >
                    <td className="py-4 px-6 text-sm">
                      <input
                        type="checkbox"
                        className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                        checked={selectedRows.some((row) => row.warehouse_code === inv.warehouse_code && row.commodity_name === inv.commodity_name)}
                        onChange={() => handleSelectRow(inv)}
                      />
                    </td>
                    <td className="py-4 px-6 text-sm text-gray-700 font-medium">
                      {inv.state_name}
                    </td>
                    <td className="py-4 px-6 text-sm text-gray-700">
                      {inv.district_name}
                    </td>
                    <td className="py-4 px-6 text-sm text-blue-700 font-medium">
                      {inv.commodity_name}
                    </td>
                    <td className="py-4 px-6 text-sm">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                        {inv.scheme_name}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-sm text-gray-700">
                      {inv.season_name}
                    </td>
                    <td className="py-4 px-6 text-sm text-gray-700">
                      <div className="flex flex-col">
                        <span className="font-semibold text-gray-900">
                          {inv.warehouse_name}
                        </span>
                        <span className="text-xs text-blue-500 mt-1">
                          {inv.warehouse_code}
                        </span>
                      </div>
                    </td>
                    <td className="py-4 px-6 text-sm text-gray-700 text-center font-bold">
                      {inv.age_of_stock || 'N/A'}
                    </td>
                    {/* <td className="py-4 px-6 text-sm">
                      <span className="inline-block px-2 py-1 rounded-lg bg-green-50 text-green-700 font-bold border border-green-100">
                        {inv.total_quantity}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-sm">
                      <span className="inline-block px-2 py-1 rounded-lg bg-red-50 text-red-700 font-bold border border-red-100">
                        {inv.total_blocked_quantity || '0.0000'}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-sm">
                      <span className="inline-block px-2 py-1 rounded-lg bg-blue-50 text-blue-700 font-bold border border-blue-100">
                        {inv.total_current_quantity || '0.0000'}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-sm">
                      <span className="inline-block px-2 py-1 rounded-lg bg-yellow-50 text-yellow-700 font-bold border border-yellow-100">
                        {inv.total_withdrawn_quantity || '0.0000'}
                      </span>
                    </td> */}

                    <td className="py-4 px-6 text-sm">
                      <div className="flex flex-col gap-1">
                        <div className="whitespace-nowrap"><span className="text-gray-500 text-xs">Total:</span> <span className="inline-block px-2 py-0.5 rounded-lg bg-green-50 text-green-700 font-bold border border-green-100">{inv.total_quantity}</span></div>
                        <div className="whitespace-nowrap"><span className="text-gray-500 text-xs">Blocked:</span> <span className="inline-block px-2 py-0.5 rounded-lg bg-red-50 text-red-700 font-bold border border-red-100">{inv.total_blocked_quantity || '0.0000'}</span></div>
                        <div className="whitespace-nowrap"><span className="text-gray-500 text-xs">Current:</span> <span className="inline-block px-2 py-0.5 rounded-lg bg-blue-50 text-blue-700 font-bold border border-blue-100"> {inv.total_current_quantity || '0.0000'}</span></div>
                        <div className="whitespace-nowrap"><span className="text-gray-500 text-xs">Withdrawn:</span> <span className="inline-block px-2 py-0.5 rounded-lg bg-gray-50 text-gray-700 font-bold border border-gray-100"> {inv.total_withdrawn_quantity || '0.0000'}</span></div>
                      </div>
                    </td>
                    <td className="py-4 px-6 text-sm">
                      <div className="flex flex-col gap-1">
                        <div className="whitespace-nowrap"><span className="text-gray-500 text-xs">Total:</span> <span className="text-gray-700 font-medium">{inv.total_bags}</span></div>
                        <div className="whitespace-nowrap"><span className="text-gray-500 text-xs">Blocked:</span> <span className="inline-block px-2 py-0.5 rounded-lg bg-red-50 text-red-700 font-bold border border-red-100">{inv.total_blocked_bags}</span></div>
                        <div className="whitespace-nowrap"><span className="text-gray-500 text-xs">Current:</span> <span className="inline-block px-2 py-0.5 rounded-lg bg-blue-50 text-blue-700 font-bold border border-blue-100">{inv.total_current_bags}</span></div>
                        <div className="whitespace-nowrap"><span className="text-gray-500 text-xs">Withdrawn:</span> <span className="inline-block px-2 py-0.5 rounded-lg bg-gray-50 text-gray-700 font-bold border border-gray-100">{inv.total_withdrawal_bags}</span></div>
                      </div>
                    </td>
                  </motion.tr>
                ))
              ) : (
                <tr>
                  <td colSpan="13" className="text-center py-12 text-gray-500 font-medium">
                    🚫 No inventory found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* Pagination */}
      <div className="flex justify-center mt-6 gap-2 md:gap-3 items-center text-sm md:text-base">
        <button
          onClick={() => page > 1 && setPage(page - 1)}
          disabled={page === 1}
          className="px-3 md:px-4 py-1 md:py-2 bg-gray-200 hover:bg-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Prev
        </button>
        <span>
          Page {page} of {totalPages || 1}
        </span>
        <button
          onClick={() => page < totalPages && setPage(page + 1)}
          disabled={page === totalPages}
          className="px-3 md:px-4 py-1 md:py-2 bg-gray-200 hover:bg-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next
        </button>
      </div>
    </div>
  );
}
